/**
 *
 *  User Profile Page
 *  Packages Component
 *
 *  Created by Vladimir Shishlyannikov on 06/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import Modal from "react-modal";
import { useAppState } from "../../../state";
import Transition, {
  ENTERED,
  ENTERING,
  EXITED,
  EXITING,
} from "react-transition-group/Transition";

// Components
import Button from "../../Button/Button";
import ScrollToTopOnMount from "../../ScrollToTopOnMount/ScrollToTopOnMount";
import RepeatBookingFlowPopup from "../../RepeatBookingFlowPopup/RepeatBookingFlowPopup";
import BookingFlowPanelHeader from "../../BookingFlow/BookingFlowPanelHeader/BookingFlowPanelHeader";
import RepeatBookingFlowPanel from "../../RepeatBookingFlowPanel/RepeatBookingFlowPanel";
import TruBeDrawer from "../../TruBeDrawer/TruBeDrawer";

// Hooks
import useTheme from "../../ThemeProvider/useTheme/useTheme";

// Icons
import SessionTypeIcon from "../SessionTypeIcon";
import SessionTypeIconGreyedOut from "../../../icons/SessionTypeIconGreyedOut";
import CloseIcon from "../../../icons/CloseIcon";
import CheckNiceIcon from "../../../icons/CheckNiceIcon";

// Utils
import { getMomentWithTz } from "../../../utils/date";
import { clientWidth, toQueryString } from "../../../utils";
import { modalStyles } from "../../../constants";

const transitionClasses = {
  [ENTERED]: "booking-flow-popup-transition-entered",
  [ENTERING]: "booking-flow-popup-transition-entering",
  [EXITING]: "booking-flow-popup-transition-exiting",
  [EXITED]: "booking-flow-popup-transition-exited",
};

const Package = ({ packageInfo, onClick }) => {
  const packageBalance = Array.from({
    length: packageInfo?.metadata.initialBalance,
  });
  const sessionsLeft = packageInfo?.metadata.balance;

  const handleBookClick = useCallback(() => {
    onClick && onClick(packageInfo);
  }, [packageInfo, onClick]);

  return (
    <div className="tbk-mb-2 tbk-flex tbk-flex-col tbk-rounded-2xl tbk-bg-basic-white tbk-p-2 tbk-shadow-trube md:tbk-p-3 lg:tbk-mb-0">
      <div className="tbk-flex">
        <div className="tb-profile-picture tbk-mr-2 tbk-mb-0.5 tbk-h-22 tbk-shrink-0 tbk-grow-0 tbk-basis-22 md:tbk-mr-2 md:tbk-h-28 md:tbk-w-28 md:tbk-basis-28">
          <img
            className="tb-profile-picture-img img-responsive tbk-h-full tbk-w-full tbk-w-full tbk-rounded-lg tbk-object-cover md:tbk-relative md:tbk-mb-0 md:tbk-mr-0"
            itemProp="image"
            src={
              packageInfo?.metadata.partnerPhotoWithBackground ||
              packageInfo?.metadata.partnerPhoto
            }
            alt={`${packageInfo?.metadata.partnerFirstName} ${packageInfo?.metadata.partnerLastName} photo`}
          />
        </div>
        <div>
          <div className="tbk-mb-2 tbk-flex tbk-items-center">
            {sessionsLeft ? (
              <SessionTypeIcon className="tbk-mr-1 tbk-drop-shadow-trube" />
            ) : (
              <SessionTypeIconGreyedOut className="tbk-mr-1 tbk-text-blue-grey" />
            )}
            <div className="tbk-text-h3-subtitle md:tbk-text-title-bold tbk-text-primary">
              Session Package
            </div>
          </div>
          <div className="tbk-relative tbk-flex tbk-grow tbk-justify-between">
            <div className="tbk-mb-2 md:tbk-mb-2">
              <div className="tbk-text-secondary lg:tbk-text-main tbk-mb-1 tbk-text-blue-dark">
                {packageInfo?.metadata.recentBooking &&
                  packageInfo?.metadata.recentBooking.product.name}{" "}
                <a
                  className="tbk-block tbk-text-blue-dark tbk-underline md:tbk-inline-block"
                  href={`/partners/${packageInfo?.metadata.partnerId}`}
                >
                  with {packageInfo?.metadata.partnerFirstName}{" "}
                  {packageInfo?.metadata.partnerLastName}
                </a>
              </div>
              <div className="tbk-mb-1_ tbk-flex tbk-items-center">
                <div
                  className={`tbk-text-small md:tbk-text-main ${
                    packageInfo?.metadata.balance
                      ? "tbk-text-blue-grey"
                      : "tbk-text-blue-grey"
                  }`}
                >
                  Sessions balance: {packageInfo?.metadata.balance} left
                </div>
              </div>
            </div>

            {/*<div className="tbk-ml-1 tbk-mr-1 tbk--mt-4 tbk-hidden tbk-h-40 tbk-shrink-0 tbk-grow-0 tbk-basis-40 md:tbk-flex" />*/}
          </div>
        </div>
      </div>

      <div className="tbk-mb-2 tbk-flex tbk-justify-evenly">
        {packageBalance.map((item, index) => (
          <div
            className={`tbk-mr-[2px] tbk-h-2 tbk-grow ${
              index === 0 ? "tbk-rounded-l-lg" : ""
            } ${
              index === packageBalance.length - 1 ? "tbk-rounded-r-lg" : ""
            } ${
              index < packageInfo?.metadata.balance
                ? "tbk-bg-coral"
                : "tbk-bg-blue-grey-light"
            }`}
          />
        ))}
      </div>
      <div className={`tbk-grid tbk-grid-cols-2 tbk-gap-2`}>
        <Link
          to={`/partners/${packageInfo?.metadata.partnerId}`}
          className={"tbk-block tbk-grow"}
        >
          <Button
            size={"md"}
            color={"coral"}
            className="tbk-w-full tbk-grow"
            // className={`tbk-flex tbk-items-center tbk-rounded-lg tbk-px-2 tbk-text-green tbk-shadow-trube hover:tbk-bg-green hover:tbk-text-basic-white`}
            // onMouseEnter={handleMouseEnter}
            // onMouseOut={handleMouseOut}
            // onClick={handleBookClick}
          >
            Book
          </Button>
        </Link>
        <Link
          to={`/partners/${packageInfo?.metadata.partnerId}`}
          className={"tbk-grow"}
        >
          <Button
            variant={"secondary"}
            size={"md"}
            // color={"coral"}
            className="tbk-px-2_ tbk-h-full tbk-w-full tbk-rounded-lg"
            //onClick={handleBookWithMeClick}
          >
            <div className="tbk-px-1">View profile</div>
          </Button>
        </Link>
      </div>
    </div>
  );
};

const Packages = ({ bundles, onStartBookingClick }) => {
  const history = useHistory();
  const { theme } = useTheme();

  const {
    user,
    packages,
    loadBooking,
    loadCustomerPackages,
    partnerAvailability,
    partnerAvailabilityLoaded,
    loadPartnerAvailability,
    clearPartnerAvailabilityLoaded,
    clearPartnerSummaryReceive,
    isFetching,
  } = useAppState();

  const [infoModalTitle, setInfoModalTitle] = useState(
    "Verification Email Has Been Sent"
  );
  const [infoModalMsg, setInfoModalMsg] = useState(
    `We sent you a link at ${
      user && user.email
    }. Please, open the link to verify your account`
  );
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const [nowTime] = useState(getMomentWithTz(new Date()));
  const [availabilityCalendarMin] = useState(
    getMomentWithTz(new Date()).startOf("day").add(330 /*05:30*/, "minute")
  );
  const [availabilityCalendarMax] = useState(
    getMomentWithTz(new Date())
      .startOf("day")
      .add(1410 /*23:30*/, "minute")
      .add(2, "month")
  );
  const [booking, setBooking] = useState(null);
  const [_directBookingTime, setDirectBookingTime] = useState(undefined);
  const [isRepeatBookingFlowPopupOpen, setIsRepeatBookingFlowPopupOpen] =
    useState(false);
  const [isRepeatBookingFlowPanelOpen, setIsRepeatBookingFlowPanelOpen] =
    useState(false);
  const [
    isRepeatBookingFlowPanelFullyOpen,
    setIsRepeatBookingFlowPanelFullyOpen,
  ] = useState(false);

  const infoModalResolveRef = useRef(null);
  const panelRef = useRef(null);
  const repeatBookingFlowPopupRef = useRef(null);
  const repeatBookingFlowPanelRef = useRef(null);

  useEffect(() => {
    if (user) {
      loadCustomerPackages(user.id, {
        page: 0,
        size: 10,
        sort: ["status,asc", "id,desc"],
      });
    }
    clearPartnerSummaryReceive(); // To prevent showing previously loaded partner in Checkout
  }, [user]);

  const openInfoModal = useCallback((msg = infoModalMsg) => {
    return new Promise((resolve, reject) => {
      infoModalResolveRef.current = resolve;

      setIsInfoModalOpen(true);

      setInfoModalTitle("Verification Email has been Sent");
      setInfoModalMsg(msg);
    });
  }, []);

  const closeInfoModal = useCallback(() => {
    infoModalResolveRef.current();
    infoModalResolveRef.current = null;

    setIsInfoModalOpen(false);
  }, []);

  const sendBooking = useCallback(() => {
    var urlParams = {
      // postal_code: booking.location.code.replace("CA ", ""),
      location: `${booking.location.latitude},${booking.location.longitude}`,
      address: booking.location.address,
      partner: booking.partner.id,
      product: booking.product.id,
      start_time: _directBookingTime,
      detail: booking.location.detail,
    };

    if (booking.location.gymName) {
      urlParams.place = booking.location.code; // Booking for Gym
    } else {
      urlParams.postal_code = booking.location.code.replace("CA ", "");
      // urlParams.place_name = booking.location.name;
    }

    history.push(`/checkout?${toQueryString(urlParams)}`);
  }, [history, booking, _directBookingTime]);

  const handleDirectBookingTimeChange = useCallback(
    (dateTime, isTimeSelected) => {
      // Analytics.trackEvent(TRAINERS_LIST_PAGE_CATEGORY, '', EVENT_DATETIME_CHANGE);

      setDirectBookingTime(dateTime ? dateTime.getTime() : undefined);
    },
    []
  );

  const closeRepeatBookingFlowPopup = useCallback(() => {
    setIsRepeatBookingFlowPopupOpen(false);
    clearPartnerSummaryReceive(); // To prevent showing previously loaded partner in Checkout
    setBooking(null);
  }, []); // eslint-disable-line

  const closeRepeatBookingFlowPanel = useCallback(() => {
    setIsRepeatBookingFlowPanelOpen(false);
    setIsRepeatBookingFlowPanelFullyOpen(false);
    clearPartnerSummaryReceive(); // To prevent showing previously loaded partner in Checkout
    setBooking(null);
  }, []); // eslint-disable-line

  const handleBottomReached = useCallback(() => {
    if (isRepeatBookingFlowPanelOpen) {
      setIsRepeatBookingFlowPanelOpen(false);
    }
  }, [isRepeatBookingFlowPanelOpen]);

  const handleFullyOpen = useCallback(() => {
    setIsRepeatBookingFlowPanelFullyOpen(true);
  }, []);

  const handleRepeatBookingFlowCtaClick = useCallback(() => {
    sendBooking();
  }, [sendBooking]);

  const handleBookClick = useCallback((pckg) => {
    setBooking(pckg.recentBooking);

    if (pckg.recentBooking) {
      loadPartnerAvailability(pckg.recentBooking.partner.id); // Time slots
    }

    if (clientWidth() < theme.responsive.breakpoints.lg) {
      setIsRepeatBookingFlowPanelOpen(true);
    } else {
      setIsRepeatBookingFlowPopupOpen(true);
    }
  }, []);

  const _bundles = packages;

  return (
    <>
      <ScrollToTopOnMount />
      <div className="tbk-mt-4_ lg:tbk-mt-5_ lg:tbk-grid lg:tbk-grid-cols-account-cards lg:tbk-gap-5">
        {_bundles && _bundles.length ? (
          _bundles.map((pckg) => (
            <Package packageInfo={pckg} onClick={handleBookClick} />
          ))
        ) : (
          <div className="tbk-mb-2 tbk-rounded-2xl tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-bg-grey-light tbk-py-2 tbk-px-2 md:tbk-mb-0 md:tbk-px-8 md:tbk-py-7">
            <div className="tbk-text-h3-subtitle tbk-mb-1 tbk-text-blue-grey">
              You have no packages yet
            </div>
            <div className="tbk-text-secondary tbk-mb-3 tbk-text-blue-grey">
              To buy a package — choose the amount of sessions in the checkout.
            </div>
            <Link to="/partners">
              <Button color="coral" size="md" className="tbk-w-full">
                Start Booking
              </Button>
            </Link>
          </div>
        )}
      </div>
      <TruBeDrawer
        attachRef={panelRef}
        allowDragging={true}
        isOpen={isRepeatBookingFlowPanelOpen}
        containerStyle={{
          width: "100%",
          height: "100%",
          overflowY: "auto",
        }}
        title={BookingFlowPanelHeader}
        titleElementProps={{
          isOnline: false,
          partnerSummary: booking && booking.partner,
          partnerLoaded: booking && booking.partner,
          onClose: closeRepeatBookingFlowPanel,
        }}
        onFullyOpen={handleFullyOpen}
        onBottomReached={handleBottomReached}
        onClose={closeRepeatBookingFlowPanel}
      >
        {isRepeatBookingFlowPanelOpen &&
          clientWidth() < theme.responsive.breakpoints.lg && (
            <RepeatBookingFlowPanel
              ref={repeatBookingFlowPanelRef}
              className=""
              isFullyOpen={isRepeatBookingFlowPanelFullyOpen}
              region={user ? user.region : process.env.REACT_APP_REGION}
              min={availabilityCalendarMin.toDate()}
              max={availabilityCalendarMax.toDate()}
              bookingTime={_directBookingTime}
              partnerSummary={booking && booking.partner}
              partnerLoaded={booking && booking.partner}
              partnerAvailability={partnerAvailability}
              partnerAvailabilityLoaded={partnerAvailabilityLoaded}
              clearPartnerAvailabilityLoaded={clearPartnerAvailabilityLoaded}
              onDateTimeSelect={handleDirectBookingTimeChange}
              onClose={closeRepeatBookingFlowPanel}
              onCtaClick={handleRepeatBookingFlowCtaClick}
            />
          )}
      </TruBeDrawer>
      <Transition
        nodeRef={repeatBookingFlowPopupRef}
        in={
          clientWidth() >= theme.responsive.breakpoints.lg &&
          isRepeatBookingFlowPopupOpen
        }
        timeout={300}
        // addEndListener={this.handleTransitionEnd}
      >
        {(status, innerProps) => (
          <RepeatBookingFlowPopup
            //{...innerProps}
            ref={repeatBookingFlowPopupRef}
            className={`tbk-transition-transform tbk-duration-300 ${
              status === ENTERING || status === EXITED
                ? "tbk-translate-x-full tbk-opacity-5"
                : status === EXITING
                ? "tbk-translate-x-full tbk-opacity-5"
                : "tbk-translate-x-0 tbk-opacity-100"
            } ${transitionClasses[status]}`}
            region={user ? user.region : process.env.REACT_APP_REGION}
            sessionType={"OFFLINE"}
            userId={user && user.id}
            min={availabilityCalendarMin.toDate()}
            max={availabilityCalendarMax.toDate()}
            bookingTime={_directBookingTime}
            partnerSummary={booking && booking.partner}
            partnerLoaded={booking && booking.partner}
            partnerAvailability={partnerAvailability}
            partnerAvailabilityLoaded={partnerAvailabilityLoaded}
            clearPartnerAvailabilityLoaded={clearPartnerAvailabilityLoaded}
            onDateTimeSelect={handleDirectBookingTimeChange}
            onClose={closeRepeatBookingFlowPopup}
            onCtaClick={handleRepeatBookingFlowCtaClick}
          />
        )}
      </Transition>
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isInfoModalOpen}
        // onAfterOpen={afterOpenInfoModal}
        onRequestClose={closeInfoModal}
        style={
          clientWidth() < theme.responsive.breakpoints.sm
            ? modalStyles.infoMobile
            : Object.assign(
                {},
                { ...modalStyles.info },
                { content: { ...modalStyles.info.content, width: "480px" } }
              )
        }
        contentLabel="Info Modal"
      >
        <div className="modal-info tbk-flex tbk-flex-col tbk-items-center">
          <button
            onClick={closeInfoModal}
            className="tbk-absolute tbk-top-2 tbk-right-2 tbk-z-10 tbk-hidden tbk-text-coral"
          >
            <CloseIcon />
          </button>
          <h3 className="tbk-text-title-bold tbk-mt-0 tbk-mb-1 tbk-text-center tbk-text-grey-main">
            {infoModalTitle}
          </h3>

          <p className="tbk-text-main tbk-mb-4 tbk-max-w-full tbk-text-center tbk-text-grey-main">
            {infoModalMsg}
          </p>
          <CheckNiceIcon size={64} className="tbk-mb-4 tbk-text-green" />
          <Button className="tbk-w-full" onClick={closeInfoModal}>
            Okay
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default Packages;
